import type { ValidatePatientResponseWithScheduledAppointments } from "@/lib/__generated/api";

/**
 * Reasons v2/account/validate tells us a booking will be refused. Each maps
 * onto a dialog in useErrorDialogConfig.
 */
export type EligibilityBlock =
  | "existingPatient_with_appt"
  | "existingPatient_no_appt"
  | "newPatient_with_appt"
  | "cannotSchedule";

/**
 * Single source of truth for "may this patient book online?".
 *
 * `canSchedule` is the server's authoritative, feature-flag-aware verdict.
 * Existing patients can schedule certain visit reasons at enabled facilities,
 * so neither `newPatient` nor `hasScheduledAppointment` is a safe standalone
 * client-side block - they only pick which refusal to show.
 */
export function getEligibilityBlock(
  resp: ValidatePatientResponseWithScheduledAppointments | null | undefined,
): EligibilityBlock | null {
  if (resp?.canSchedule !== false) return null;
  if (resp.newPatient === false)
    return resp.hasScheduledAppointment ? "existingPatient_with_appt" : "existingPatient_no_appt";
  if (resp.newPatient === true && resp.hasScheduledAppointment === true) {
    return "newPatient_with_appt";
  }
  // canSchedule is still authoritative, so an unmatched refusal must keep blocking.
  return "cannotSchedule";
}

/** True when validation has positively refused this patient. */
export function isEligibilityBlocked(
  resp: ValidatePatientResponseWithScheduledAppointments | null | undefined,
): boolean {
  return getEligibilityBlock(resp) !== null;
}
